import { Grid, Button, Typography } from '@mui/material'
import { Share } from '@promoboxx/graphql-gateway-types'
import I18n from 'i18n-js'
import moment from 'moment'
import { makeStyles } from 'tss-react/mui'

import LinkChild, { LegacyLinkChild } from '@src/lib/LinkChild'
import { useFeatureFlag } from '@src/lib/useFeatureFlag'

import organicCampaignPaths from '../content/organicCampaignPaths'
import reportingPaths from '../reporting/reportingPaths'

const useStyles = makeStyles()((theme) => ({
  loader: {
    display: 'flex',
    justifyContent: 'center',
    alignContent: 'center',
    padding: theme.spacing(12),
  },
  image: {
    width: '100%',
  },
  disablePaddingTop: {
    paddingTop: 0,
  },
  statsContainer: {
    paddingTop: theme.spacing(6),
  },
  previewContainer: {
    paddingTop: theme.spacing(6),
    backgroundColor: theme.palette.background.default,
  },
  actionsContainer: {
    position: 'absolute',
    right: 0,
  },

  card: {
    width: 'auto',
  },
  statsList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  statsListItem: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
    margin: theme.spacing(0, 2),
  },
  statsNumber: {
    color: theme.palette.text.secondary,
    fontSize: theme.typography.h5.fontSize,
    marginBottom: theme.spacing(2),
  },
  statsLabel: {
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
  },
  shareLink: {
    marginTop: theme.spacing(6),
    color: theme.palette.primary.dark,
    textTransform: 'uppercase',
    textAlign: 'center',
    display: 'block',
    textDecoration: 'none',
    fontSize: theme.typography.caption.fontSize,
  },
  mobilePadding: {
    paddingTop: 28,
    [theme.breakpoints.up('sm')]: {
      paddingTop: 0,
    },
  },
}))

const ShareStats: React.FC<
  React.PropsWithChildren<{ share: Share; retailerAccountSlug: string }>
> = ({ share, retailerAccountSlug }) => {
  const shareFlowUpliftEnabled = useFeatureFlag('share-flow-uplift')
  const { classes } = useStyles()
  const shareUrl = share.post_url || share.tweet_url || ''
  let {
    pbxx_views = 0,
    pbxx_actions = 0,
    pbxx_consumer_clicks = null,
  } = share.internal_stats || {}
  let clicks =
    share.channel === 'facebook'
      ? share.link_clicks || 0
      : pbxx_consumer_clicks || 0

  return (
    <Grid container alignItems="center" spacing={5}>
      {share.channel !== 'instagram' || shareFlowUpliftEnabled ? (
        <Grid item xs={12}>
          <ul className={classes.statsList}>
            {pbxx_views !== null && pbxx_views >= 0 && (
              <li className={classes.statsListItem}>
                <span className={classes.statsNumber}>{pbxx_views}</span>
                <span className={classes.statsLabel}>
                  {I18n.t('shareCard.views')}
                </span>
              </li>
            )}
            {pbxx_actions !== null && pbxx_actions >= 0 && (
              <li className={classes.statsListItem}>
                <span className={classes.statsNumber}>{pbxx_actions}</span>
                <span className={classes.statsLabel}>
                  {I18n.t('shareCard.actions')}
                </span>
              </li>
            )}
            {
              <li className={classes.statsListItem}>
                <span className={classes.statsNumber}>{clicks}</span>
                <span className={classes.statsLabel}>
                  {I18n.t('shareCard.clicks')}
                </span>
              </li>
            }
          </ul>
        </Grid>
      ) : null}

      <Grid item xs={12}>
        <LinkChild
          to={reportingPaths.organicCampaignDetails.build({
            retailerAccountSlug,
            campaignId: share.campaign?.link_id || '',
            channel: share.channel || '',
          })}
        >
          <Button fullWidth color="primary" variant="contained" size="large">
            {I18n.t('shareCard.viewReporting')}
          </Button>
        </LinkChild>
      </Grid>
      <Grid item xs={12}>
        <Typography align="center">
          {I18n.t('shareCard.sharedOn', {
            user:
              share.share_mode === 'auto_share'
                ? I18n.t('shareCard.autoShare')
                : `${share.created_by?.first_name} ${share.created_by?.last_name}`,
            shareDate: moment(share.executed_at).format('MM/DD/YY @ h:mm a'),
          })}
        </Typography>
      </Grid>
      {shareFlowUpliftEnabled && shareUrl && (
        <Grid item xs={12}>
          <a
            className={classes.shareLink}
            href={shareUrl}
            target="_blank"
            rel="noreferrer"
          >
            {I18n.t('shareCard.viewShare', {
              channel: share?.channel,
            })}
          </a>
        </Grid>
      )}
      <Grid item xs={12}>
        <LegacyLinkChild
          className={classes.shareLink}
          to={`${organicCampaignPaths.details.build({
            retailerAccountSlug,
            campaignId: share.campaign?.link_id || '',
            channel: share.channel || '',
          })}?shareSettingId=${
            share.share_setting_id
          }&shareSettingBrandId=${share.campaign?.brand?.id}`}
        >
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- LinkChild does it for us */}
          <a>{I18n.t('shareCard.shareAgain')}</a>
        </LegacyLinkChild>
      </Grid>
    </Grid>
  )
}

export default ShareStats
